import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // Charger le panier depuis localStorage au démarrage
  useEffect(() => {
    const savedCart = localStorage.getItem('jamaalstore_cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
      }
    }
  }, []);

  // Sauvegarder le panier dans localStorage à chaque modification
  useEffect(() => {
    localStorage.setItem('jamaalstore_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Ajouter un produit au panier
  const addToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item._id === product._id);
      
      if (existingItem) {
        // Si le produit existe déjà, augmenter la quantité
        toast.success(`Quantité mise à jour pour ${product.pName}`, {
          icon: "🛒"
        });
        return prevItems.map((item) =>
          item._id === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Sinon, ajouter le nouveau produit avec quantité 1
        toast.success(`${product.pName} ajouté au panier !`, {
          icon: "✅"
        });
        return [...prevItems, { ...product, quantity: 1 }];
      }
    });
  };

  // Retirer un produit du panier
  const removeFromCart = (productId, productName) => {
    setCartItems((prevItems) => prevItems.filter((item) => item._id !== productId));
    toast.info(productName ? `${productName} retiré du panier` : 'Produit retiré du panier', {
      icon: "🗑️"
    });
  };

  // Mettre à jour la quantité d'un produit
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item._id === productId ? { ...item, quantity } : item
      )
    );
  };

  // Vider le panier
  const clearCart = () => {
    const itemCount = cartItems.length;
    setCartItems([]);
    if (itemCount > 0) {
      toast.warning(`Panier vidé (${itemCount} ${itemCount > 1 ? 'produits' : 'produit'})`, {
        icon: "🧹"
      });
    }
  };

  // Calculer le total du panier
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.pOffer > 0 
        ? item.pPrice - (item.pPrice * item.pOffer) / 100 
        : item.pPrice;
      return total + price * item.quantity;
    }, 0);
  };

  // Obtenir le nombre total d'articles
  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
